import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import axios, { AxiosError } from 'axios'
import type { TokenProvider } from './tokenProvider'
import { isAccessTokenExpiredError } from '@/libs/responseError'
import { setupCache } from 'axios-cache-interceptor'
import { FRONTEND_ID } from '@/libs/frontendId'
import type { ClientInfo } from '@/libs/deviceApi/clientInfo'

const NO_AUTH_URLS = ['/api/v1/user/geolocation']

type LocaleGetter = () => string

function applyAuthHeader(config: InternalAxiosRequestConfig<unknown>, accessToken: string) {
  config.headers['Authorization'] = `Bearer ${accessToken}`
}

function applyXOperaRewardsFrontendHeader(config: InternalAxiosRequestConfig<unknown>) {
  config.headers['X-OperaRewards-Frontend'] = 'true'
}

function applyXFrontendIdHeader(config: InternalAxiosRequestConfig<unknown>) {
  config.headers['X-Frontend-Id'] = FRONTEND_ID
}

function applyXClientVersionHeader(
  config: InternalAxiosRequestConfig<unknown>,
  clientVersion: string
) {
  config.headers['X-ClientVersion'] = clientVersion
}

function applyXOperaRewardsEntrypointHeader(
  config: InternalAxiosRequestConfig<unknown>,
  entrypointOnPageLoad: string | null
) {
  if (entrypointOnPageLoad !== null) {
    config.headers['X-OperaRewards-Entrypoint'] = entrypointOnPageLoad
  }
}

function applyAcceptLanguageHeader(config: InternalAxiosRequestConfig<unknown>, locale: string) {
  config.headers['Accept-Language'] = locale
}

async function onRequest(
  config: InternalAxiosRequestConfig<unknown>,
  tokenProvider: TokenProvider,
  clientInfo: ClientInfo,
  entrypointOnPageLoad: string | null,
  locale: string
) {
  const url = config.url

  applyXOperaRewardsFrontendHeader(config)
  applyXOperaRewardsEntrypointHeader(config, entrypointOnPageLoad)
  applyXClientVersionHeader(config, clientInfo.version)
  applyAcceptLanguageHeader(config, locale)
  applyXFrontendIdHeader(config)

  for (const noAuthUrl of NO_AUTH_URLS) {
    if (url?.includes(noAuthUrl)) {
      return config
    }
  }

  if (url === undefined) {
    throw new Error('Unexpected axios config without url ' + config)
  }

  // Try to obtain access token only if client is other than unknown.
  if (clientInfo.name !== 'unknown') {
    const accessToken = await tokenProvider.getValidAccessToken({ forceRefresh: false })
    applyAuthHeader(config, accessToken)
  }

  return config
}

async function onResponseError(error: unknown, tokenProvider: TokenProvider): Promise<unknown> {
  if (!(error instanceof AxiosError)) {
    // Not an axios error, ignore.
    return Promise.reject(error)
  }

  if (!error.config) {
    // Axios config missing.
    return Promise.reject(error)
  }

  if (isAccessTokenExpiredError(error)) {
    const accessToken = await tokenProvider.getValidAccessToken({ forceRefresh: true })
    const newConfig = { ...error.config }
    applyAuthHeader(newConfig, accessToken)
    // By returning an axios.request we instruct axios to retry request.
    return axios.request(newConfig)
  }

  return Promise.reject(error)
}

export function axiosForMobileMissions(
  tokenProvider: TokenProvider,
  clientInfo: ClientInfo,
  entrypointOnPageLoad: string | null,
  localeGetter: LocaleGetter,
  useCache: boolean = true
): AxiosInstance {
  const axiosInstance = axios.create()

  axiosInstance.interceptors.request.use((config) =>
    onRequest(config, tokenProvider, clientInfo, entrypointOnPageLoad, localeGetter())
  )
  axiosInstance.interceptors.response.use(undefined, (error) =>
    onResponseError(error, tokenProvider)
  )

  if (!useCache) {
    return axiosInstance
  }

  // Setup 1 second cache for every cacheable request
  const axiosInstanceWithCache = setupCache(axiosInstance, { ttl: 1_000 })
  return axiosInstanceWithCache
}
